import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdvancePayment } from "@/types";
import { toast } from "react-toastify";
import {
  createAdvancePayment,
  deleteAdvancePayment,
  fetchAdvancePayment,
  updateAdvancePayment,
  discountVouchers,
} from "./thunks";

interface PaginatedAdvancePayment {
  data: AdvancePayment[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface AdvancePaymentState {
  advancePayment: PaginatedAdvancePayment;
  loading: boolean;
  error: string | null;
}

const initialState: AdvancePaymentState = {
  advancePayment: { data: [] },
  loading: false,
  error: null,
};

const AdvancePaymentSlice = createSlice({
  name: "advancePayment",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.advancePayment = { data: [] };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchAdvancePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAdvancePayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.advancePayment = { data: payload.data };
          } else {
            // Dados paginados
            state.advancePayment = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchAdvancePayment.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })
      // Create
      .addCase(createAdvancePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createAdvancePayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.advancePayment.data.push(action.payload.data);
          toast.success("Vale cadastrado com sucesso!");
        },
      )
      .addCase(createAdvancePayment.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Não foi possível criar o vale, tente novamente!");
      })

      // Update
      .addCase(updateAdvancePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateAdvancePayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.advancePayment.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.advancePayment.data[index] = action.payload.data;
          }
          toast.success("Vale atualizado com sucesso!");
        },
      )
      .addCase(updateAdvancePayment.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        console.log("error", state.error);
        toast.error("Error");
      })

      // Delete
      .addCase(deleteAdvancePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteAdvancePayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.advancePayment.data = state.advancePayment.data.filter(
            (s) => String(s.id) != action.payload.data.id,
          );
          toast.success("Vale excluído com sucesso!");
        },
      )
      .addCase(deleteAdvancePayment.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Discount Vouchers
      .addCase(discountVouchers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        discountVouchers.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          // Atualizar os vales que foram dados baixa
          state.advancePayment.data = state.advancePayment.data.map((vale) => {
            if (action.payload.data.updated_ids?.includes(vale.id)) {
              return { ...vale, was_discounted: 1 };
            }
            return vale;
          });
          toast.success("Baixa realizada com sucesso!");
        },
      )
      .addCase(discountVouchers.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao dar baixa nos vales, tente novamente!");
      });
  },
});

export default AdvancePaymentSlice.reducer;
